import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { format } from 'date-fns';
import { Heart, Plus, Edit, Trash2, ChevronDown } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { TreatmentSupportRequest } from '@/types';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { useTreatmentSupportRequests } from '@/hooks/use-treatment-support';

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-[#7dda55]/20 text-[#7dda55]',
  approved: 'bg-[#7dda55]/20 text-[#7dda55]',
  rejected: 'bg-[#004250]/20 text-[#004250]',
  completed: 'bg-[#004250]/20 text-[#004250]',
};

const STATUS_OPTIONS: TreatmentSupportRequest['status'][] = [
  'pending',
  'approved',
  'rejected',
  'completed',
];

export function ProviderDashboard() {
  const { t, dir } = useLanguage();
  const { requests, loading, refreshRequests, updateRequest, deleteRequest } = useTreatmentSupportRequests();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editRequest, setEditRequest] = useState<TreatmentSupportRequest | null>(null);
  const [form, setForm] = useState<Partial<TreatmentSupportRequest>>({});
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusModalRequestId, setStatusModalRequestId] = useState<string | null>(null);
  const [statusModalValue, setStatusModalValue] = useState<TreatmentSupportRequest['status']>('pending');

  // Add Request
  const handleAddRequest = () => {
    if (!form.userId || !form.details || typeof form.cost !== 'number') return;
    updateRequest({
      ...form,
      id: (Math.random() * 100000).toFixed(0),
      status: 'pending',
      createdAt: new Date(),
      providerNotes: form.providerNotes || '',
    });
    setIsAddOpen(false);
    setForm({});
  };

  // Edit Request
  const handleEditRequest = () => {
    if (!editRequest || !form.userId || !form.details || typeof form.cost !== 'number') return;
    updateRequest({
      ...form,
      id: editRequest.id,
      userId: form.userId!,
      details: form.details!,
      providerNotes: form.providerNotes || '',
      cost: form.cost
    });
    setIsEditOpen(false);
    setEditRequest(null);
    setForm({});
  };

  // Open Edit Modal
  const openEditModal = (request: TreatmentSupportRequest) => {
    setEditRequest(request);
    setForm({ ...request });
    setIsEditOpen(true);
  };

  // Open status modal
  const openStatusModal = (request: TreatmentSupportRequest) => {
    setStatusModalRequestId(request.id);
    setStatusModalValue(request.status);
    setStatusModalOpen(true);
  };

  // Save status change
  const handleStatusModalSave = () => {
    if (!statusModalRequestId) return;
    updateRequest({
      ...requests.find(r => r.id === statusModalRequestId)!,
      status: statusModalValue
    });
    setStatusModalOpen(false);
    setStatusModalRequestId(null);
  };

  return (
    <Card dir={dir}>
      <CardHeader className={`border-b bg-muted/50 flex flex-row items-center justify-between ${dir === 'rtl' ? 'flex-row-reverse justify-end' : ''}`}>
        <CardTitle className={`text-2xl font-bold tracking-tight flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse ' : ''}`}> 
          {dir === 'rtl' ? (
            <>
              {t('dashboard.provider.title', 'Manage Treatment Support Requests')}
              <Heart className="h-7 w-7 text-primary" />
            </>
          ) : (
            <>
              <Heart className="h-7 w-7 text-primary" />
              {t('dashboard.provider.title', 'Manage Treatment Support Requests')}
            </>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6" dir={dir}>
        <div className="rounded-md border overflow-x-auto" dir={dir}>
          <Table dir={dir}>
            <TableHeader>
              <TableRow>
                <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>{t('dashboard.provider.name', 'Name')}</TableHead>
                <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>{t('dashboard.provider.details', 'Details')}</TableHead>
                <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>{t('dashboard.provider.cost', 'Cost')}</TableHead>
                <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>{t('dashboard.provider.status', 'Status')}</TableHead>
                <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>{t('dashboard.provider.notes', 'Notes')}</TableHead>
                <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>{t('dashboard.provider.created', 'Created')}</TableHead>
                <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>{t('dashboard.provider.actions', 'Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>{request.userId}</TableCell>
                  <TableCell>{request.details}</TableCell>
                  <TableCell>{request.cost}</TableCell>
                  <TableCell className="relative overflow-visible">
                    <button
                      className={`px-2 py-1 rounded-full text-xs font-semibold focus:outline-none flex items-center gap-1 ${STATUS_COLORS[request.status]}`}
                      onClick={() => openStatusModal(request)}
                    >
                      {t(`dashboard.provider.statuses.${request.status}`, request.status.charAt(0).toUpperCase() + request.status.slice(1))}
                    </button>
                  </TableCell>
                  <TableCell>{request.providerNotes}</TableCell>
                  <TableCell>{format(request.createdAt, 'PPP')}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => openEditModal(request)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => deleteRequest(request.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      {/* Status Change Modal */}
      <Dialog open={statusModalOpen} onOpenChange={setStatusModalOpen}>
        <DialogContent className={`border-0 w-1/2 max-w-md mx-auto ${dir === 'rtl' ? 'text-right' : ''}`} dir={dir}>
          <DialogHeader>
            <DialogTitle>{t('dashboard.provider.changeStatus', 'Change Status')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {STATUS_OPTIONS.map((status) => (
              <button
                key={status}
                className={`w-full ${dir === 'rtl' ? 'text-right justify-end flex-row-reverse' : 'text-left'} px-4 py-2 rounded font-semibold flex items-center gap-2 ${STATUS_COLORS[status]}${status === statusModalValue ? ' ring-2 ring-[#004250]' : ''}`}
                onClick={() => {
                  if (!statusModalRequestId) return;
                  updateRequest({
                    ...requests.find(r => r.id === statusModalRequestId)!,
                    status: status
                  });
                  setStatusModalOpen(false);
                  setStatusModalRequestId(null);
                }}
              >
                {t(`dashboard.provider.statuses.${status}`, status.charAt(0).toUpperCase() + status.slice(1))}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Request Modal */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent dir={dir}>
          <DialogHeader>
            <DialogTitle>{t('dashboard.provider.editRequest', 'Edit Request')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input placeholder={t('dashboard.provider.name', 'Name')} value={form.userId || ''} onChange={e => setForm(f => ({ ...f, userId: e.target.value }))} dir={dir} />
            <Input placeholder={t('dashboard.provider.details', 'Details')} value={form.details || ''} onChange={e => setForm(f => ({ ...f, details: e.target.value }))} dir={dir} />
            <Input placeholder={t('dashboard.provider.cost', 'Cost')} type="number" value={form.cost ?? ''} onChange={e => setForm(f => ({ ...f, cost: Number(e.target.value) }))} dir={dir} />
            <Input placeholder={t('dashboard.provider.notes', 'Notes')} value={form.providerNotes || ''} onChange={e => setForm(f => ({ ...f, providerNotes: e.target.value }))} dir={dir} />
            <Button onClick={handleEditRequest}>{t('dashboard.provider.saveChanges', 'Save Changes')}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
} 